import { createContext, useContext, useEffect, useMemo } from "react";
import {
  APPLICATION_IDS,
  CUIJ_PATTERN,
  canAccessApplication,
  canAccessTool,
  classifyExpedienteIdentifier,
  createFullSuiteAccess,
  TOOL_IDS,
} from "@tribunalea/shared";
import {
  AuthenticatedArea,
  AuthPortal,
  SuiteAccountMenuControls,
  useAccessToken,
  useRememberSuiteProduct,
  useSuiteAccountAvatar,
  useTribunaleaAccess,
} from "@tribunalea/auth/react";
import { Justiniano } from "@tribunalea/justiniano";
import {
  InstitutionalAccessPage,
  useActivityTracking,
  useRouter,
} from "@tribunalea/ui";
import { AppShell } from "./components/AppShell.jsx";
import { createApi } from "./lib/api.js";
import { NotificationsPage } from "./pages/NotificationsPage.jsx";
import { RuleFormPage } from "./pages/RuleFormPage.jsx";
import { DeliveriesPage } from "./pages/DeliveriesPage.jsx";
import { TeamPage } from "./pages/TeamPage.jsx";
import { LawyersPage } from "./pages/LawyersPage.jsx";
import { ProfilePage } from "./pages/ProfilePage.jsx";
import { ExpedientesPage } from "./pages/ExpedientesPage.jsx";
import { ExpedienteSearchPage } from "./pages/ExpedienteSearchPage.jsx";
import { DailyListsPage } from "./pages/DailyListsPage.jsx";
import { DraftsPage } from "./pages/DraftsPage.jsx";
import { FollowUpsPage } from "./pages/FollowUpsPage.jsx";
import { WorkflowTemplatesPage } from "./pages/WorkflowTemplatesPage.jsx";
import {
  WorkflowTemplateEditorPage,
} from "./pages/WorkflowTemplateEditorPage.jsx";
import { WorkflowRecordsPage } from "./pages/WorkflowRecordsPage.jsx";
import {
  WorkflowRecordFormPage,
} from "./pages/WorkflowRecordFormPage.jsx";
import { IntegrationsPage } from "./pages/IntegrationsPage.jsx";
import {
  integrationDefinitionForRoute,
} from "./integrations/registry.jsx";
import {
  ExpedienteSourceDetailRoute,
} from "./integrations/expediente-detail-registry.jsx";
import ExtensionWidgetPage from "./pages/ExtensionWidgetPage.jsx";
import {
  ExternalExpedienteRedirectPage,
} from "./pages/ExternalExpedienteRedirectPage.jsx";
import { praxisJustinianoRenderers } from "./justiniano/PraxisJustiniano.jsx";
import { praxisLandingPath } from "./lib/praxis-landing-route.js";

const ApiContext = createContext(null);
const AppContext = createContext({ authEnabled: false });

export const useApi = () => useContext(ApiContext);
export const useApp = () => useContext(AppContext);

const accountCreditsUrl = `${import.meta.env.VITE_ACCOUNT_URL || (
  import.meta.env.DEV
    ? "http://localhost:5175"
    : "https://account.tribunalea.com"
)}/creditos`;
const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";

async function loadConversationAssignmentTargets(api) {
  const [active, archived] = await Promise.all([
    api.listExpedientes(),
    api.listExpedientes({ archived: true }),
  ]);
  return [...new Map([...active, ...archived].map((expediente) => [
    expediente.cuij,
    {
      id: expediente.cuij,
      label: expediente.caratula || expediente.cuij,
      secondary: [
        expediente.cuij,
        expediente.numero ? `N.º ${expediente.numero}` : null,
        expediente.archivedAt ? "Archivado" : null,
      ].filter(Boolean).join(" · "),
    },
  ])).values()];
}

export function praxisToolForPath(path) {
  if (/^\/(?:expedientes|buscar-expedientes)(?:\/|$)/.test(path)) {
    return TOOL_IDS.PRAXIS_EXPEDIENTES;
  }
  if (/^\/seguimientos(?:\/|$)/.test(path)) return TOOL_IDS.PRAXIS_FOLLOW_UPS;
  if (/^\/flujos(?:\/|$)/.test(path)) return TOOL_IDS.PRAXIS_WORKFLOWS;
  if (/^\/listas(?:\/|$)/.test(path)) return TOOL_IDS.PRAXIS_DAILY_LISTS;
  if (/^\/escritos(?:\/|$)/.test(path)) return TOOL_IDS.PRAXIS_WRITING;
  if (/^\/(?:alertas|historial)(?:\/|$)/.test(path)) {
    return TOOL_IDS.PRAXIS_NOTIFICATIONS;
  }
  if (/^\/integraciones(?:\/|$)/.test(path)) {
    return TOOL_IDS.PRAXIS_INTEGRATIONS;
  }
  if (/^\/abogados(?:\/|$)/.test(path)) {
    return TOOL_IDS.PRAXIS_ADMINISTRATION;
  }
  return null;
}

function AccountRedirect() {
  useEffect(() => {
    window.location.replace(accountCreditsUrl);
  }, []);
  return <main className="page">Abriendo Cuenta…</main>;
}

function ProductRoutes({
  access,
  accessError = null,
  accessLoading = false,
  accountAvatar,
  authEnabled,
  clerkControls,
  getAccessToken,
  reloadAccess = null,
}) {
  const api = useApi();
  const { path, navigate } = useRouter();
  const organizationWorkspace = access?.workspace?.type === "organization";
  const applicationAllowed = canAccessApplication(
    access,
    APPLICATION_IDS.PRAXIS,
  );
  const landingPath = praxisLandingPath(access);
  const redirectFromUnavailableLanding = Boolean(
    path === "/expedientes"
    && applicationAllowed
    && !canAccessTool(access, TOOL_IDS.PRAXIS_EXPEDIENTES)
    && landingPath
    && landingPath !== path,
  );
  const resolvedPath = redirectFromUnavailableLanding ? landingPath : path;
  const toolId = praxisToolForPath(resolvedPath);
  const controlRoute = ["/perfil", "/cuenta/creditos"].includes(resolvedPath)
    || (resolvedPath === "/equipo" && organizationWorkspace);
  const routeAllowed = controlRoute || (
    applicationAllowed && (!toolId || canAccessTool(access, toolId))
  );

  useEffect(() => {
    if (
      accessLoading
      || accessError
      || !redirectFromUnavailableLanding
    ) return;
    navigate(landingPath, { replace: true });
  }, [
    accessError,
    accessLoading,
    landingPath,
    navigate,
    redirectFromUnavailableLanding,
  ]);

  useActivityTracking({
    apiUrl,
    getAccessToken,
    appId: APPLICATION_IDS.PRAXIS,
    toolId: routeAllowed ? toolId : null,
    enabled: authEnabled && !accessLoading && !accessError && routeAllowed,
    scopeKey: access?.workspace?.id || "personal",
  });
  if (accessLoading || accessError) {
    return (
      <InstitutionalAccessPage
        error={accessError}
        loading={accessLoading}
        onRetry={reloadAccess}
      />
    );
  }
  const expedienteRouteMatch = /^\/(?:expedientes|seguimientos)\/([^/]+)(?:\/(?:(?:informacion|actuaciones|documentos|checklist|plazos|notificaciones|recordatorios)|escritos(?:\/[^/]+)?))?\/?$/.exec(
    resolvedPath,
  );
  const expedienteRouteIdentifier = expedienteRouteMatch
    ? decodeURIComponent(expedienteRouteMatch[1])
    : null;
  const activeExpediente = expedienteRouteIdentifier
    ? classifyExpedienteIdentifier(expedienteRouteIdentifier)
    : null;
  const externalExpedienteMatch = /^\/expedientes\/externo\/([^/]+)\/?$/.exec(
    resolvedPath,
  );
  const externalExpedienteId = externalExpedienteMatch
    ? decodeURIComponent(externalExpedienteMatch[1])
    : null;
  const integrationRoute = integrationDefinitionForRoute(resolvedPath);
  let page;
  if (resolvedPath === "/equipo" && !organizationWorkspace) {
    navigate(landingPath || "/perfil", { replace: true });
    page = null;
  }
  else if (!routeAllowed) {
    page = <InstitutionalAccessPage label={toolId ? "Esta herramienta" : "Praxis"} />;
  }
  else if (externalExpedienteId) {
    page = <ExternalExpedienteRedirectPage externalId={externalExpedienteId} />;
  }
  else if (resolvedPath === "/buscar-expedientes") page = <ExpedienteSearchPage />;
  else if (resolvedPath === "/expedientes") page = <ExpedientesPage />;
  else if (resolvedPath === "/seguimientos") page = <FollowUpsPage />;
  else if (resolvedPath === "/flujos" || resolvedPath === "/flujos/") {
    page = <WorkflowTemplatesPage />;
  }
  else if (resolvedPath === "/flujos/nuevo" || resolvedPath === "/flujos/nuevo/") {
    page = <WorkflowTemplateEditorPage />;
  }
  else if (/^\/flujos\/[a-f\d]{24}\/configuracion\/?$/i.test(resolvedPath)) {
    page = (
      <WorkflowTemplateEditorPage
        templateId={resolvedPath.split("/").filter(Boolean)[1]}
      />
    );
  }
  else if (/^\/flujos\/[a-f\d]{24}\/registros\/nuevo\/?$/i.test(resolvedPath)) {
    page = (
      <WorkflowRecordFormPage
        templateId={resolvedPath.split("/").filter(Boolean)[1]}
      />
    );
  }
  else if (/^\/flujos\/[a-f\d]{24}\/registros\/[a-f\d]{24}\/ver\/?$/i.test(resolvedPath)) {
    const routeParts = resolvedPath.split("/").filter(Boolean);
    page = (
      <WorkflowRecordFormPage
        templateId={routeParts[1]}
        recordId={routeParts[3]}
        readOnly
      />
    );
  }
  else if (/^\/flujos\/[a-f\d]{24}\/registros\/[a-f\d]{24}\/?$/i.test(resolvedPath)) {
    const routeParts = resolvedPath.split("/").filter(Boolean);
    page = (
      <WorkflowRecordFormPage
        templateId={routeParts[1]}
        recordId={routeParts[3]}
      />
    );
  }
  else if (/^\/flujos\/[a-f\d]{24}\/?$/i.test(resolvedPath)) {
    page = (
      <WorkflowRecordsPage
        templateId={resolvedPath.split("/").filter(Boolean)[1]}
      />
    );
  }
  else if (resolvedPath === "/listas") page = <DailyListsPage />;
  else if (resolvedPath === "/integraciones") page = <IntegrationsPage />;
  else if (integrationRoute) {
    const IntegrationPage = integrationRoute.page;
    page = <IntegrationPage />;
  }
  else if (resolvedPath === "/alertas") page = <NotificationsPage />;
  else if (resolvedPath === "/alertas/nueva") page = <RuleFormPage />;
  else if (/^\/alertas\/[^/]+\/editar$/.test(resolvedPath)) page = <RuleFormPage />;
  else if (/^\/escritos(?:\/[^/]+)?\/?$/.test(resolvedPath)) page = <DraftsPage />;
  else if (
    activeExpediente
  ) {
    page = (
      <ExpedienteSourceDetailRoute
        expediente={activeExpediente}
        routeKey={resolvedPath.split("/")[1]}
      />
    );
  }
  else if (resolvedPath === "/alertas/historial") page = <DeliveriesPage />;
  else if (resolvedPath === "/historial") {
    navigate("/alertas/historial", { replace: true });
    page = <DeliveriesPage />;
  }
  else if (resolvedPath === "/equipo") page = <TeamPage />;
  else if (resolvedPath === "/abogados") page = <LawyersPage />;
  else if (resolvedPath === "/perfil") page = <ProfilePage />;
  else if (resolvedPath === "/cuenta/creditos") page = <AccountRedirect />;
  else {
    navigate(landingPath || "/expedientes", { replace: true });
    page = null;
  }

  return (
    <AppContext.Provider value={{ authEnabled }}>
      <AppShell
        access={access}
        accountAvatar={accountAvatar}
        api={api}
        authEnabled={authEnabled}
        clerkControls={clerkControls}
      >
        {page}
      </AppShell>
      {routeAllowed && canAccessTool(access, TOOL_IDS.PRAXIS_ASSISTANT) && (
      <Justiniano
        allowAttachments
        appId="praxis"
        appName="Praxis"
        apiUrl={apiUrl}
        getAccessToken={getAccessToken}
        welcome="Puedo buscar y revisar expedientes, ayudarte con tus causas y operar sobre Mis expedientes."
        suggestions={[
          "Mostrame mis expedientes",
          "Buscá el expediente de Amarilla",
          "Mostrame mis expedientes archivados",
        ]}
        renderers={praxisJustinianoRenderers}
        context={{ navigate }}
        conversationAssignment={{
          contextType: "expediente",
          label: "un expediente",
          pluralLabel: "expedientes",
          activeTarget: activeExpediente
            && CUIJ_PATTERN.test(activeExpediente.identifier)
            ? {
                id: activeExpediente.identifier,
                label: `Expediente ${activeExpediente.identifier}`,
                secondary: activeExpediente.identifier,
              }
            : null,
          loadTargets: () => loadConversationAssignmentTargets(api),
        }}
        appContext={{
          route: resolvedPath,
          activeEntity: activeExpediente
            ? {
                type: "expediente",
                id: activeExpediente.identifier,
                label: `Expediente ${activeExpediente.identifier}`,
                source: activeExpediente.sourceId,
              }
            : null,
        }}
      />
      )}
    </AppContext.Provider>
  );
}

function ClerkApp() {
  const getToken = useAccessToken();
  const accountAvatar = useSuiteAccountAvatar();
  const api = useMemo(() => createApi(getToken), [getToken]);
  const accessState = useTribunaleaAccess({ apiUrl });
  useRememberSuiteProduct("praxis", {
    enabled: Boolean(accessState.access) && canAccessApplication(
      accessState.access,
      APPLICATION_IDS.PRAXIS,
    ),
  });

  return (
    <ApiContext.Provider value={api}>
      <ProductRoutes
        access={accessState.access}
        accessError={accessState.error}
        accessLoading={accessState.loading}
        accountAvatar={accountAvatar}
        authEnabled
        clerkControls={<SuiteAccountMenuControls />}
        getAccessToken={getToken}
        reloadAccess={accessState.reload}
      />
    </ApiContext.Provider>
  );
}

function SignInScreen() {
  return (
    <AuthPortal
      productName="PRAXIS"
      headline="Tu práctica jurídica, siempre al día."
      description="Centralizá los movimientos que importan y compartí alertas con todo tu estudio."
      proofPoints={[
        "Seguimiento preciso",
        "Equipos coordinados",
        "Canales configurables",
      ]}
    />
  );
}

export default function App({ authEnabled }) {
  const extensionWidget = window.location.pathname === "/extension-widget";

  if (extensionWidget) {
    return <ExtensionWidgetPage />;
  }

  if (authEnabled) {
    return (
      <AuthenticatedArea
        signedOut={<SignInScreen />}
      >
        <ClerkApp />
      </AuthenticatedArea>
    );
  }

  const api = createApi(null);
  const access = createFullSuiteAccess();
  return (
    <ApiContext.Provider value={api}>
      <ProductRoutes
        access={access}
        authEnabled={false}
        getAccessToken={null}
      />
    </ApiContext.Provider>
  );
}
